import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { ONLINE_ORDERING_ENABLED, SHOW_PRICES } from "@/lib/features";
import { ImageLightbox } from "@/components/ImageLightbox";
import { useOrderingStores } from "@/lib/ordering";
import { MapPin, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AllergyNotice } from "@/components/AllergyNotice";
import { PartyPlatterCustomizer } from "@/components/PartyPlatterCustomizer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(null);
  const [customizing, setCustomizing] = useState(null);
  const category = params.get("category") || "All";
  const { addItem, count, storeId: cartStoreId, setStoreId } = useCart();
  const { stores: orderingStores } = useOrderingStores();
  const { user } = useAuth();
  const isStaff = user && user.role !== "customer";
  const canOrder = ONLINE_ORDERING_ENABLED && orderingStores.length > 0 && !isStaff;
  const orderStore = orderingStores.find((s) => s.id === cartStoreId) || orderingStores[0];

  useEffect(() => {
    if (orderStore && orderStore.id !== cartStoreId) setStoreId(orderStore.id);
  }, [orderStore, cartStoreId, setStoreId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/menu", orderStore ? { params: { store_id: orderStore.id } } : undefined).then((r) => setItems(r.data)),
      api.get("/menu/category-order").then((r) => setCategoryOrder(r.data.order || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [orderStore?.id]);

  const categories = useMemo(() => {
    const seen = new Set(items.map((i) => i.category));
    const ordered = categoryOrder.filter((c) => seen.has(c));
    const rest = Array.from(seen).filter((c) => !ordered.includes(c));
    return ["All", ...ordered, ...rest];
  }, [items, categoryOrder]);

  const filtered = items.filter((i) => {
    if (category !== "All" && i.category !== category) return false;
    if (query && !`${i.name} ${i.description}`.toLowerCase().includes(query.toLowerCase())) return false;
    return i.is_available;
  });

  const setCat = (c) => {
    if (c === "All") { params.delete("category"); } else { params.set("category", c); }
    setParams(params);
  };

  const handleAdd = (item) => {
    if (item.platter_config) {
      setCustomizing(item);
      return;
    }
    addItem(item);
    toast.success(`${item.name} added to cart`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="mb-10">
        <p className="eyebrow mb-2">Our Menu</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="menu-title">Chef-rolled, daily.</h1>
        <p className="mt-4 text-neutral-600 max-w-xl leading-relaxed">From classic sushi rolls to rice bowls and ramen — there's something for everyone.</p>
        {canOrder && (
          <>
          <div className="mt-6 inline-flex flex-wrap items-center gap-3 rounded-2xl bg-neutral-950 text-white px-5 py-3" data-testid="ordering-banner">
            <MapPin className="w-4 h-4 text-[hsl(var(--primary))]"/>
            <span className="text-sm"><b>Order online for pickup</b> at {orderStore.name} <span className="text-neutral-400">· {orderStore.address}</span></span>
            {count > 0 && <Link to="/cart" className="ml-1 inline-flex items-center gap-1 text-xs font-semibold bg-[hsl(var(--primary))] rounded-full px-3 py-1" data-testid="ordering-banner-cart"><ShoppingBag className="w-3 h-3"/>Cart ({count})</Link>}
          </div>
          {orderingStores.length > 1 && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="menu-store-picker">
              <span className="text-xs text-neutral-500">Pickup location:</span>
              {orderingStores.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStoreId(s.id)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${s.id === orderStore.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "border-neutral-200 bg-white hover:border-neutral-400"}`}
                  data-testid={`menu-store-choice-${s.id}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search menu…" className="pl-9 rounded-full h-11" data-testid="menu-search"/>
        </div>
      </div>

      <AllergyNotice minimal />

      <div className="mb-5 sm:hidden" data-testid="menu-category-mobile-selector">
        <Select value={category} onValueChange={setCat}>
          <SelectTrigger
            className="h-11 rounded-xl bg-white text-left"
            data-testid="menu-category-mobile-trigger"
          >
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent data-testid="menu-category-mobile-options">
            {categories.map((itemCategory) => (
              <SelectItem key={itemCategory} value={itemCategory}>
                {itemCategory === "All" ? "All categories" : itemCategory}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div
        className="mb-8 hidden grid-cols-3 gap-2 sm:grid md:grid-cols-6 lg:grid-cols-9"
        data-testid="menu-category-filters"
      >
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`min-h-10 rounded-full px-3 py-2 text-center text-sm font-medium leading-tight transition-colors ${category === c ? "bg-[hsl(var(--primary))] text-white" : "border border-neutral-200 bg-white hover:border-neutral-400"}`}
            aria-pressed={category === c}
            data-testid={`cat-filter-${c}`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={`menu-skeleton-${i}`} className="h-72 rounded-2xl bg-neutral-100 animate-pulse"/>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-neutral-500" data-testid="menu-empty">No items found.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((item) => (
            <div key={item.id} className="group bg-white rounded-2xl overflow-hidden border border-neutral-200/70 card-hover" data-testid={`menu-item-${item.id}`}>
              <button type="button" onClick={() => setPreview(item)} className="block w-full aspect-[4/3] overflow-hidden bg-neutral-100 cursor-zoom-in focus:outline-none" aria-label={`View photo of ${item.name}`} data-testid={`menu-image-${item.id}`}>
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
              </button>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display font-semibold text-lg leading-tight">{item.name}</h3>
                  {SHOW_PRICES && (
                    <span className="font-display font-bold text-[hsl(var(--primary))]" data-testid={`menu-price-${item.id}`}>${item.price.toFixed(2)}</span>
                  )}
                </div>
                {item.contains_raw_seafood && (
                  <span
                    className="mt-2 inline-flex rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900"
                    data-testid={`menu-raw-seafood-${item.id}`}
                  >
                    Contains raw seafood
                  </span>
                )}
                <p className="text-sm text-neutral-500 mt-1.5 line-clamp-2">{item.description}</p>
                {item.platter_config && (
                  <p
                    className="mt-3 text-xs font-semibold text-[hsl(var(--primary))]"
                    data-testid={`platter-preorder-card-notice-${item.id}`}
                  >
                    Preorder only · at least one day ahead
                  </p>
                )}
                {canOrder && (
                  <Button
                    onClick={() => handleAdd(item)}
                    className="mt-4 w-full rounded-xl bg-neutral-900 hover:bg-[hsl(var(--primary))]"
                    data-testid={`add-to-cart-${item.id}`}
                  >
                    <Plus className="w-4 h-4 mr-1"/>
                    {item.platter_config ? "Customize platter" : "Add to cart"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <ImageLightbox item={preview} onClose={() => setPreview(null)}/>
      <PartyPlatterCustomizer
        item={customizing}
        open={!!customizing}
        onClose={() => setCustomizing(null)}
        onSave={(platter) => {
          addItem(customizing, 1, platter);
          toast.success(`${customizing.name} added to cart`);
          setCustomizing(null);
        }}
      />
    </div>
  );
}
